"use client";

import React, { useMemo, useCallback } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { TrustIcon } from "@/components/icons/generated/TrustIcon";
import { PublicStudentHeader } from "./PublicStudentContent/PublicStudentHeader";
import PublicReportSection from "./PublicReportSection";
import PublicResultsTab from "./PublicStudentContent/tabs/PublicResultsTab";
import { useEnumeration } from "@/api/enums/enumsApi";
import { formatDateTime } from "@/shared/dateFormat";
import type { ReportStudent, ReportAssignment, ReportCertificate } from "@/types/endpoints/reports/GetAssignmentReportInfo";
import type { AssignmentReportDataResponse, ReportIdentity, ReportViolation } from "@/types/endpoints/reports/GetAssignmentReportData";
import type { GetAssignmentReportResultsResponse } from "@/types/endpoints/reports/GetAssignmentReportResults";

interface ReportPrintViewProps {
  student: ReportStudent;
  assignment: ReportAssignment;
  certificate?: ReportCertificate | null;
  reportData?: AssignmentReportDataResponse;
  resultsData?: GetAssignmentReportResultsResponse;
  selectedAttemptId: number;
}

/** Represents a single identity image with its URL and type */
interface IdentityImage {
  url: string;
  type: "screenshot" | "id" | "face";
  identityId: number;
}

/**
 * Get all image URLs from an identity object.
 * Returns array of images: screenshot field, screenshots.id, and screenshots.face
 */
const getIdentityImages = (identity: ReportIdentity): IdentityImage[] => {
  const images: IdentityImage[] = [];

  if (identity.screenshot) {
    images.push({ url: identity.screenshot, type: "screenshot", identityId: identity.id });
  }

  if (identity.screenshots && !Array.isArray(identity.screenshots)) {
    const screenshots = identity.screenshots as { id?: string; face?: string };
    if (screenshots.id) {
      images.push({ url: screenshots.id, type: "id", identityId: identity.id });
    }
    if (screenshots.face) {
      images.push({ url: screenshots.face, type: "face", identityId: identity.id });
    }
  }

  return images;
};

/**
 * Get the primary image URL from a violation object.
 * Prefer screenshot, fallback to first screenshots[] item.
 */
const getViolationImageUrl = (violation: ReportViolation): string | null => {
  if (typeof violation.screenshot === "string" && violation.screenshot.length > 0) {
    return violation.screenshot;
  }

  if (Array.isArray(violation.screenshots) && violation.screenshots.length > 0) {
    const first = violation.screenshots[0] as unknown;
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      const candidate =
        (first as { url?: string; thumb_url?: string; screenshot?: string }).url ??
        (first as { url?: string; thumb_url?: string; screenshot?: string }).thumb_url ??
        (first as { url?: string; thumb_url?: string; screenshot?: string }).screenshot;
      if (typeof candidate === "string" && candidate.length > 0) return candidate;
    }
  }

  return null;
};

/**
 * ReportPrintView - dedicated print-only layout for PDF export.
 * Renders all report content flat (no tabs, no modals, no video section).
 * Use forwardRef so the parent can pass a ref for react-to-print.
 */
export const ReportPrintView = React.forwardRef<HTMLDivElement, ReportPrintViewProps>(
  function ReportPrintView(
    { student, assignment, certificate, reportData, resultsData, selectedAttemptId },
    ref
  ) {
    const t = useTranslations();
    const locale = useLocale();
    const { data: actionTypes = [] } = useEnumeration("AssignmentActionType");

    // Compute full name and photo the same way as PublicStudentContent/index.tsx
    const fullName =
      (student.user
        ? [student.user.firstname, student.user.lastname].filter(Boolean).join(" ")
        : "") ||
      student.name ||
      `Student #${student.id}`;

    const photo =
      student.user?.photo || student.user?.photo_thumb || student.user?.avatar;

    // Find the selected attempt
    const selectedAttempt = useMemo(
      () => reportData?.entity?.attempts?.find((a) => a.id === selectedAttemptId),
      [reportData?.entity?.attempts, selectedAttemptId]
    );

    // Collect all identity images
    const allIdentityImages = useMemo(() => {
      const identities = reportData?.entity?.identities || [];
      return identities.flatMap((identity) => getIdentityImages(identity));
    }, [reportData?.entity?.identities]);

    // Sort violations descending by created_at
    const sortedViolations = useMemo(() => {
      const violations = reportData?.entity?.violations?.data || [];
      return [...violations].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }, [reportData?.entity?.violations?.data]);

    const getViolationTitle = useCallback(
      (violation: ReportViolation): string => {
        if (violation.description && violation.description.trim().length > 0) {
          return violation.description;
        }

        if (violation.action_type) {
          const normalized = violation.action_type.toLowerCase();
          const found = actionTypes.find(
            (type: { raw: string; name: string }) => type.raw.toLowerCase() === normalized
          );
          if (found?.name) return found.name;
          return violation.action_type.replace(/_/g, " ");
        }

        return t("label-assignment-violations");
      },
      [actionTypes, t]
    );

    return (
        <div ref={ref} className="print-only">
        {/* Header bar */}
        <div className="w-full bg-muted px-4 py-4 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <TrustIcon size={38} className="text-[#0059FF]" />
            <div className="min-w-0">
              <h1 className="text-[15px] font-medium text-gray-900 truncate leading-tight">
                {assignment.name || assignment.title}
              </h1>
              <p className="text-sm text-gray-500 leading-tight">
                {t("label-report-description")}
              </p>
            </div>
          </div>
        </div>

        {/* Content sections */}
        <div className="px-3 space-y-4">
          {/* Student Header */}
          <PublicStudentHeader
            fullName={fullName}
            photo={photo}
            startedAt={selectedAttempt?.started_at ?? null}
            finishedAt={selectedAttempt?.finished_at ?? null}
            score={student.points ?? null}
            isExternal={assignment.type === "external"}
          />

          {/* Risk Level Card - uses PublicReportSection which fetches AI reports and calculates trust score */}
          <PublicReportSection
            attemptId={selectedAttemptId}
            credibility={selectedAttempt?.credibility}
            assignmentType={assignment.type}
            credibilityVerdict={reportData?.entity?.credibility_verdict}
          />

          {/* Results Section */}
          <PublicResultsTab
            quizComponents={reportData?.entity?.quiz_components}
            results={reportData?.entity?.results}
            resultsData={resultsData}
            studentAssessments={reportData?.entity?.student_assessments}
            totalPoints={student.points}
            isPointSystemEnabled={assignment.is_point_system_enabled}
            assessmentName={assignment.name || assignment.title}
            certificate={certificate}
          />

          {/* Identity Photos Section */}
          <Card className="gap-2 print-section">
            <CardHeader className="print-section-header">
              <CardTitle className="text-sm font-medium">
                {t("label-proctoring-photo-head-identity")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {allIdentityImages.length > 0 ? (
                <div className="grid grid-cols-4 gap-3">
                  {allIdentityImages.map((image, i) => (
                    <div
                      key={`${image.identityId}-${image.type}`}
                      className="relative overflow-hidden rounded-md border bg-muted/40"
                    >
                      <img
                        src={image.url}
                        alt={`Photo ${i + 1}`}
                        width={300}
                        height={200}
                        className="object-cover w-full h-24"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("label-empty-found")}</p>
              )}
            </CardContent>
          </Card>

          {/* Violations Section */}
          <Card className="gap-2 print-section">
            <CardHeader className="print-section-header">
              <CardTitle className="text-sm font-medium">
                {t("label-assignment-violations")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("label-assignment-violations-subtitle")}
              </p>
            </CardHeader>
            <CardContent>
              {sortedViolations.length > 0 ? (
                <div className="divide-y">
                  {sortedViolations.slice(0, 100).map((violation) => {
                    const imageUrl = getViolationImageUrl(violation);
                    const title = getViolationTitle(violation);
                    return (
                      <div
                        key={violation.id}
                        className="flex items-start gap-4 py-4 print-violation-row"
                      >
                        {imageUrl && (
                          <div className="h-16 w-28 rounded-lg overflow-hidden bg-muted/50 border shrink-0">
                            <img
                              src={imageUrl}
                              alt={title}
                              className="object-cover w-full h-full"
                            />
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          <div className="text-sm font-semibold text-foreground">{title}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatDateTime(violation.created_at, locale)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("label-empty-found")}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
);

ReportPrintView.displayName = "ReportPrintView";

export default ReportPrintView;
